// C13 — Gestión de usuarios (líderes y supervisores) del scope.
//
// 🚧 PENDIENTE refactor visual a detalle (sesión 2026-05-28).
// Ajuste mínimo aplicado para coherencia con C1/C2/C3/C5/C6:
//   - Header consistente (título + subtítulo informativo + botones top-right)
//   - Tokens semánticos (text-fg / text-fg-tertiary / text-fg-secondary, sin colores hardcoded)
//   - Loading/error states alineados con el resto
//   - Filtros (si aplica) en barra horizontal con bg-surface-overlay/40
//   - Cualquier <Table>/<TR>/<TD> custom → tabla raw con clases consistentes
//
// NO se agregó lógica nueva, KPI cards con sparkline ni gráfico Recharts —
// eso queda para la revisión a detalle por pantalla.
//
// Estado original — cambios sensibles requieren razón ≥ 20 caracteres (auditoría).

import { useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import type { CoordinadorUsuariosList, UsuarioGestion } from 'schemas';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { TBody, TD, TH, THead, TR, Table } from '../../components/ui/table';
import { CoordinadorSidebar } from '../../components/layout/CoordinadorSidebar';
import { RoleLayout } from '../../components/layout/RoleLayout';
import {
  useCoordinadorUsuariosList,
  useCrearUsuario,
  useEditarUsuario,
  useReasignarLineas,
  useResetPasswordUsuario,
} from '../../lib/api/queries';
import { useAuth } from '../../lib/auth';
import { identidadUsuario } from '../../lib/identidad';
import { cn } from '../../lib/utils';

type Filtro = 'todos' | 'lider' | 'supervisor';

interface NuevoUsuarioForm {
  modo: 'cedula' | 'correo';
  email: string;
  cedula: string;
  password_temporal: string;
  password_confirm: string;
  telefono: string;
  nombre: string;
  apellido: string;
  rol: 'lider' | 'supervisor';
  linea_ids: string[];
  planta_ids: string[];
  razon: string;
}

const NUEVO_USUARIO_INICIAL: NuevoUsuarioForm = {
  modo: 'cedula',
  email: '',
  cedula: '',
  password_temporal: '',
  password_confirm: '',
  telefono: '',
  nombre: '',
  apellido: '',
  rol: 'lider',
  linea_ids: [],
  planta_ids: [],
  razon: '',
};

export function C13UsuariosPage() {
  const { state } = useAuth();
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [mostrarInactivos, setMostrarInactivos] = useState(false);
  const [modalNuevo, setModalNuevo] = useState(false);
  const [nuevoForm, setNuevoForm] = useState<NuevoUsuarioForm>(NUEVO_USUARIO_INICIAL);
  const [editing, setEditing] = useState<UsuarioGestion | null>(null);
  const [reasignando, setReasignando] = useState<UsuarioGestion | null>(null);

  const { data, isLoading, isError, error } = useCoordinadorUsuariosList();

  const usuariosFiltrados = useMemo(() => {
    if (!data?.usuarios) return [];
    return data.usuarios.filter((u) => {
      if (filtro !== 'todos' && u.rol !== filtro) return false;
      if (!mostrarInactivos && !u.activo) return false;
      return true;
    });
  }, [data?.usuarios, filtro, mostrarInactivos]);

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  if (isLoading) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-3 bg-surface-base p-6 text-fg">
        <Spinner className="size-6" />
        <p className="text-sm text-fg-secondary">Cargando usuarios…</p>
      </main>
    );
  }

  if (isError || !data) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-3 bg-surface-base p-6 text-center text-fg">
        <h1 className="text-lg font-medium text-crit">No pudimos cargar</h1>
        <p className="text-sm text-fg-secondary">
          {error instanceof Error ? error.message : 'Error desconocido'}
        </p>
      </main>
    );
  }

  return (
    <RoleLayout sidebar={<CoordinadorSidebar />} roleLabel="Coordinador">
      <div className="flex flex-col gap-4">
        <header className="flex flex-col gap-3 border-b border-border pb-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-xl font-medium text-fg">Usuarios</h1>
            <p className="mt-1 text-[11px] text-fg-tertiary">
              {data.tenant.nombre} · líderes y supervisores del scope · cambios auditados con razón
            </p>
          </div>
          <Button variant="primary" onClick={() => setModalNuevo(true)}>
            Inscribir usuario
          </Button>
        </header>

        <Card>
          <CardContent className="flex flex-wrap items-end gap-3 p-4 pt-4">
            <div className="flex flex-col gap-1">
              <Label htmlFor="c13-rol">Rol</Label>
              <Select
                id="c13-rol"
                value={filtro}
                onChange={(e) => setFiltro(e.target.value as Filtro)}
              >
                <option value="todos">Todos</option>
                <option value="lider">Líderes</option>
                <option value="supervisor">Supervisores</option>
              </Select>
            </div>
            <label className="flex items-center gap-2 pb-2 text-sm text-fg">
              <input
                type="checkbox"
                checked={mostrarInactivos}
                onChange={(e) => setMostrarInactivos(e.target.checked)}
                className="size-4"
              />
              Mostrar inactivos
            </label>
          </CardContent>
        </Card>

        {usuariosFiltrados.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-sm text-fg-tertiary">
              Sin usuarios con esos filtros.
            </CardContent>
          </Card>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Usuario</TH>
                <TH>Acceso</TH>
                <TH align="center">Rol</TH>
                <TH>Asignaciones</TH>
                <TH align="center">Estado</TH>
                <TH align="right" />
              </TR>
            </THead>
            <TBody>
              {usuariosFiltrados.map((u) => (
                <TR key={u.user_id}>
                  <TD>
                    <span className="font-medium text-fg">
                      {u.nombre} {u.apellido}
                    </span>
                  </TD>
                  <TD className="text-xs text-fg-secondary">
                    <div>{identidadUsuario(u.email).etiqueta}</div>
                    {u.telefono ? <div className="text-fg-tertiary">Tel: {u.telefono}</div> : null}
                  </TD>
                  <TD align="center">
                    <span
                      className={cn(
                        'inline-block rounded px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider',
                        u.rol === 'lider' ? 'bg-info/10 text-info' : 'bg-warn/10 text-warn',
                      )}
                    >
                      {u.rol}
                    </span>
                  </TD>
                  <TD className="text-xs text-fg-secondary">
                    {u.rol === 'lider'
                      ? `${u.lineas_asignadas.length} línea(s): ${u.lineas_asignadas
                          .map((l) => l.linea_codigo)
                          .join(', ')}`
                      : `${u.plantas_asignadas.length} planta(s): ${u.plantas_asignadas
                          .map((p) => p.planta_codigo)
                          .join(', ')}`}
                  </TD>
                  <TD align="center">
                    <span
                      className={cn(
                        'inline-block rounded px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider',
                        u.activo ? 'bg-ok/10 text-ok' : 'bg-fg/10 text-fg-tertiary',
                      )}
                    >
                      {u.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </TD>
                  <TD align="right">
                    <div className="flex justify-end gap-1">
                      {u.rol === 'lider' && (
                        <Button size="sm" variant="ghost" onClick={() => setReasignando(u)}>
                          Líneas
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => setEditing(u)}>
                        Editar
                      </Button>
                    </div>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </div>

      {modalNuevo && (
        <NuevoUsuarioDialog
          data={data}
          form={nuevoForm}
          setForm={setNuevoForm}
          onClose={() => setModalNuevo(false)}
          onCreated={() => {
            setNuevoForm(NUEVO_USUARIO_INICIAL);
            setModalNuevo(false);
          }}
        />
      )}
      {editing && <EditarUsuarioDialog usuario={editing} onClose={() => setEditing(null)} />}
      {reasignando && (
        <ReasignarDialog usuario={reasignando} data={data} onClose={() => setReasignando(null)} />
      )}
    </RoleLayout>
  );
}

// =============================================================================

function NuevoUsuarioDialog({
  data,
  form,
  setForm,
  onClose,
  onCreated,
}: {
  data: CoordinadorUsuariosList;
  form: NuevoUsuarioForm;
  setForm: Dispatch<SetStateAction<NuevoUsuarioForm>>;
  onClose: () => void;
  onCreated: () => void;
}) {
  const crear = useCrearUsuario();
  // Error inline (no toast: el toast quedaba detrás del modal).
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!form.nombre || !form.apellido || form.razon.length < 20) {
      setError('Complete nombre, apellido y razón ≥ 20 caracteres');
      return;
    }
    if (form.modo === 'correo' && !form.email) {
      setError('Ingrese el correo');
      return;
    }
    if (form.modo === 'cedula' && !/^\d{6,12}$/.test(form.cedula)) {
      setError('La cédula debe ser de 6 a 12 dígitos');
      return;
    }
    if (form.modo === 'cedula' && form.password_temporal.length < 8) {
      setError('La contraseña temporal debe tener al menos 8 caracteres');
      return;
    }
    if (form.modo === 'cedula' && form.password_temporal !== form.password_confirm) {
      setError('Las contraseñas no coinciden');
      return;
    }
    if (form.telefono.trim().length < 7) {
      setError('El teléfono es obligatorio');
      return;
    }
    if (form.rol === 'lider' && form.linea_ids.length === 0) {
      setError('Asigne al menos 1 línea');
      return;
    }
    if (form.rol === 'supervisor' && form.planta_ids.length === 0) {
      setError('Asigne al menos 1 planta');
      return;
    }
    setError(null);
    try {
      await crear.mutateAsync({
        ...(form.modo === 'correo'
          ? { email: form.email }
          : { cedula: form.cedula, password_temporal: form.password_temporal }),
        telefono: form.telefono.trim(),
        nombre: form.nombre,
        apellido: form.apellido,
        rol: form.rol,
        linea_ids: form.rol === 'lider' ? form.linea_ids : undefined,
        planta_ids: form.rol === 'supervisor' ? form.planta_ids : undefined,
        razon: form.razon,
      });
      toast.success('Usuario inscripto');
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al crear');
    }
  };

  const toggleLinea = (id: string) =>
    setForm((f) => ({
      ...f,
      linea_ids: f.linea_ids.includes(id)
        ? f.linea_ids.filter((x) => x !== id)
        : [...f.linea_ids, id],
    }));
  const togglePlanta = (id: string) =>
    setForm((f) => ({
      ...f,
      planta_ids: f.planta_ids.includes(id)
        ? f.planta_ids.filter((x) => x !== id)
        : [...f.planta_ids, id],
    }));

  return (
    <Dialog open onClose={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Inscribir usuario en el tenant</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <Label htmlFor="nu-nombre">Nombre *</Label>
              <Input
                id="nu-nombre"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="nu-apellido">Apellido *</Label>
              <Input
                id="nu-apellido"
                value={form.apellido}
                onChange={(e) => setForm({ ...form, apellido: e.target.value })}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="nu-modo">Tipo de acceso *</Label>
            <Select
              id="nu-modo"
              value={form.modo}
              onChange={(e) => setForm({ ...form, modo: e.target.value as 'cedula' | 'correo' })}
            >
              <option value="cedula">Cédula + contraseña (login directo)</option>
              <option value="correo">Correo (invitación)</option>
            </Select>
          </div>
          {form.modo === 'cedula' ? (
            <>
              <div className="flex flex-col gap-1">
                <Label htmlFor="nu-cedula">Cédula *</Label>
                <Input
                  id="nu-cedula"
                  inputMode="numeric"
                  maxLength={12}
                  value={form.cedula}
                  onChange={(e) =>
                    setForm({ ...form, cedula: e.target.value.replace(/\D/g, '').slice(0, 12) })
                  }
                  placeholder="Ej. 1098765432"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1">
                  <Label htmlFor="nu-pass">Contraseña temporal *</Label>
                  <Input
                    id="nu-pass"
                    type="password"
                    value={form.password_temporal}
                    onChange={(e) => setForm({ ...form, password_temporal: e.target.value })}
                    placeholder="mínimo 8 caracteres"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Label htmlFor="nu-pass2">Repetir contraseña *</Label>
                  <Input
                    id="nu-pass2"
                    type="password"
                    value={form.password_confirm}
                    onChange={(e) => setForm({ ...form, password_confirm: e.target.value })}
                    placeholder="repetir contraseña"
                    aria-invalid={
                      Boolean(form.password_confirm) &&
                      form.password_confirm !== form.password_temporal
                    }
                  />
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-col gap-1">
              <Label htmlFor="nu-email">Email *</Label>
              <Input
                id="nu-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value.toLowerCase() })}
                placeholder="usuario@empresa.com"
              />
            </div>
          )}
          <div className="flex flex-col gap-1">
            <Label htmlFor="nu-tel">Teléfono de contacto *</Label>
            <Input
              id="nu-tel"
              inputMode="tel"
              value={form.telefono}
              onChange={(e) => setForm({ ...form, telefono: e.target.value })}
              placeholder="Ej. 3001234567"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="nu-rol">Rol *</Label>
            <Select
              id="nu-rol"
              value={form.rol}
              onChange={(e) => setForm({ ...form, rol: e.target.value as 'lider' | 'supervisor' })}
            >
              <option value="lider">Líder</option>
              <option value="supervisor">Supervisor</option>
            </Select>
          </div>
          {form.rol === 'lider' ? (
            <div className="flex flex-col gap-1">
              <Label>Líneas a asignar *</Label>
              <div className="max-h-40 overflow-y-auto rounded border border-border p-2">
                {data.recursos.lineas.map((l) => (
                  <label key={l.id} className="flex items-center gap-2 py-1 text-sm">
                    <input
                      type="checkbox"
                      checked={form.linea_ids.includes(l.id)}
                      onChange={() => toggleLinea(l.id)}
                      className="size-4"
                    />
                    <span className="font-medium">{l.codigo}</span>
                    <span className="text-fg-tertiary">
                      {l.planta_nombre} · {l.nombre}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              <Label>Plantas a asignar *</Label>
              <div className="max-h-40 overflow-y-auto rounded border border-border p-2">
                {data.recursos.plantas.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 py-1 text-sm">
                    <input
                      type="checkbox"
                      checked={form.planta_ids.includes(p.id)}
                      onChange={() => togglePlanta(p.id)}
                      className="size-4"
                    />
                    <span className="font-medium">{p.codigo}</span>
                    <span className="text-fg-tertiary">{p.nombre}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="flex flex-col gap-1">
            <Label htmlFor="nu-razon">Razón (mínimo 20 caracteres) *</Label>
            <Input
              id="nu-razon"
              value={form.razon}
              onChange={(e) => setForm({ ...form, razon: e.target.value })}
              placeholder="Por ejemplo: solicitud de RRHH para cobertura de planta..."
            />
            <span className="text-[10px] text-fg-tertiary">{form.razon.length}/20 mínimo</span>
          </div>
        </div>
        {error ? (
          <p className="rounded-md bg-crit/10 px-3 py-2 text-sm text-crit">{error}</p>
        ) : null}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={submit} loading={crear.isPending}>
            Inscribir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================

function EditarUsuarioDialog({
  usuario,
  onClose,
}: {
  usuario: UsuarioGestion;
  onClose: () => void;
}) {
  const editar = useEditarUsuario(usuario.user_id);
  const reset = useResetPasswordUsuario(usuario.user_id);
  const [form, setForm] = useState({
    nombre: usuario.nombre,
    apellido: usuario.apellido,
    telefono: usuario.telefono ?? '',
    activo: usuario.activo,
    razon: '',
  });
  const [mostrarReset, setMostrarReset] = useState(false);
  const [pass, setPass] = useState('');
  const [passConfirm, setPassConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (form.razon.length < 20) {
      setError('Razón debe tener al menos 20 caracteres');
      return;
    }
    if (form.telefono.trim().length < 7) {
      setError('El teléfono es obligatorio');
      return;
    }
    setError(null);
    try {
      await editar.mutateAsync({
        nombre: form.nombre,
        apellido: form.apellido,
        telefono: form.telefono.trim(),
        activo: form.activo,
        razon: form.razon,
      });
      toast.success('Usuario actualizado');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    }
  };

  const submitReset = async () => {
    if (pass.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    if (pass !== passConfirm) {
      setError('Las contraseñas no coinciden');
      return;
    }
    if (form.razon.length < 20) {
      setError('Complete la razón (≥ 20 caracteres) abajo antes de reiniciar');
      return;
    }
    setError(null);
    try {
      await reset.mutateAsync({ password_temporal: pass, razon: form.razon });
      toast.success('Contraseña reiniciada');
      setPass('');
      setPassConfirm('');
      setMostrarReset(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            Editar · {usuario.nombre} {usuario.apellido}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <Label htmlFor="eu-nombre">Nombre *</Label>
              <Input
                id="eu-nombre"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="eu-apellido">Apellido *</Label>
              <Input
                id="eu-apellido"
                value={form.apellido}
                onChange={(e) => setForm({ ...form, apellido: e.target.value })}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="eu-tel">Teléfono de contacto *</Label>
            <Input
              id="eu-tel"
              inputMode="tel"
              value={form.telefono}
              onChange={(e) => setForm({ ...form, telefono: e.target.value })}
              placeholder="Ej. 3001234567"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-fg">
            <input
              type="checkbox"
              checked={form.activo}
              onChange={(e) => setForm({ ...form, activo: e.target.checked })}
              className="size-4"
            />
            Activo en este tenant
          </label>
          <div className="flex flex-col gap-1">
            <Label htmlFor="eu-razon">Razón (mínimo 20 caracteres) *</Label>
            <Input
              id="eu-razon"
              value={form.razon}
              onChange={(e) => setForm({ ...form, razon: e.target.value })}
              placeholder="Motivo del cambio (queda en auditoría)..."
            />
            <span className="text-[10px] text-fg-tertiary">{form.razon.length}/20 mínimo</span>
          </div>

          {/* Reiniciar contraseña — líderes y supervisores del scope */}
          <div className="flex flex-col gap-2 rounded-md border border-border p-3">
            {!mostrarReset ? (
              <Button variant="outline" size="sm" onClick={() => setMostrarReset(true)}>
                Reiniciar contraseña
              </Button>
            ) : (
              <>
                <p className="text-[11px] text-fg-tertiary">
                  Nueva contraseña: {usuario.nombre} ingresa con su cédula y esta clave. Usa la
                  razón de arriba; no queda visible en la plataforma.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="eu-pass">Nueva contraseña</Label>
                    <Input
                      id="eu-pass"
                      type="password"
                      value={pass}
                      onChange={(e) => setPass(e.target.value)}
                      placeholder="mínimo 8 caracteres"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="eu-pass2">Repetir contraseña</Label>
                    <Input
                      id="eu-pass2"
                      type="password"
                      value={passConfirm}
                      onChange={(e) => setPassConfirm(e.target.value)}
                      placeholder="repetir contraseña"
                      aria-invalid={Boolean(passConfirm) && passConfirm !== pass}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setMostrarReset(false);
                      setPass('');
                      setPassConfirm('');
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={submitReset}
                    loading={reset.isPending}
                  >
                    Reiniciar contraseña
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
        {error ? (
          <p className="rounded-md bg-crit/10 px-3 py-2 text-sm text-crit">{error}</p>
        ) : null}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={submit} loading={editar.isPending}>
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================

function ReasignarDialog({
  usuario,
  data,
  onClose,
}: {
  usuario: UsuarioGestion;
  data: CoordinadorUsuariosList;
  onClose: () => void;
}) {
  const reasignarLineas = useReasignarLineas(usuario.user_id);

  const [linea_ids, setLineaIds] = useState<string[]>(
    usuario.lineas_asignadas.map((l) => l.linea_id),
  );
  const [razon, setRazon] = useState('');

  const submit = async () => {
    if (razon.length < 20) {
      toast.error('Razón debe tener al menos 20 caracteres');
      return;
    }
    try {
      await reasignarLineas.mutateAsync({ linea_ids, razon });
      toast.success('Líneas actualizadas');
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error');
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Líneas asignadas · {usuario.nombre} {usuario.apellido}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <Label>Líneas (solo las de plantas en su scope)</Label>
            <div className="max-h-60 overflow-y-auto rounded border border-border p-2">
              {data.recursos.lineas.map((l) => (
                <label key={l.id} className="flex items-center gap-2 py-1 text-sm">
                  <input
                    type="checkbox"
                    checked={linea_ids.includes(l.id)}
                    onChange={() =>
                      setLineaIds((curr) =>
                        curr.includes(l.id) ? curr.filter((x) => x !== l.id) : [...curr, l.id],
                      )
                    }
                    className="size-4"
                  />
                  <span className="font-medium">{l.codigo}</span>
                  <span className="text-fg-tertiary">
                    {l.planta_nombre} · {l.nombre}
                  </span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="ra-razon">Razón (mínimo 20 caracteres) *</Label>
            <Input
              id="ra-razon"
              value={razon}
              onChange={(e) => setRazon(e.target.value)}
              placeholder="Motivo del cambio (queda en auditoría)..."
            />
            <span className="text-[10px] text-fg-tertiary">{razon.length}/20 mínimo</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={submit} loading={reasignarLineas.isPending}>
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
